import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import {
  LayoutDashboard,
  Calendar,
  Users,
  Send,
  CreditCard,
  Settings,
  ShieldCheck,
  LogOut,
} from "lucide-react";

const navItems = [
  { to: "/app/dashboard", label: "Tableau de bord", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/app/meetings", label: "Réunions", icon: Calendar, testId: "nav-meetings" },
  { to: "/app/distributions", label: "Envois", icon: Send, testId: "nav-distributions" },
  { to: "/app/team", label: "Équipe", icon: Users, testId: "nav-team" },
  { to: "/app/billing", label: "Abonnement", icon: CreditCard, testId: "nav-billing" },
  { to: "/app/settings", label: "Paramètres", icon: Settings, testId: "nav-settings" },
];

const adminNavItem = { to: "/app/admin", label: "Administration", short: "Admin", icon: ShieldCheck, testId: "nav-admin" };

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-af flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-af bg-af-card">
        <div className="px-6 py-6 border-b border-af">
          <Link to="/app/dashboard" className="flex items-center gap-3" data-testid="logo-link">
            <Logo size={52} />
            <div>
              <div className="font-display font-extrabold text-xl leading-none">AfriMeet</div>
              <div className="text-[9px] tracking-[0.18em] text-af-muted mt-1.5 font-semibold uppercase">
                Enregistre · Transcrit
              </div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all ${
                  isActive
                    ? "bg-af-subtle text-terracotta font-semibold"
                    : "text-af-secondary hover:bg-af-subtle hover:text-af"
                }`
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
          {user?.is_super_admin && (
            <NavLink
              to={adminNavItem.to}
              data-testid={adminNavItem.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all mt-4 border-t border-af pt-4 ${
                  isActive
                    ? "bg-af-subtle text-terracotta font-semibold"
                    : "text-af-secondary hover:bg-af-subtle hover:text-af"
                }`
              }
            >
              <adminNavItem.icon className="h-4 w-4" />
              {adminNavItem.label}
            </NavLink>
          )}
        </nav>
        <div className="px-3 py-4 border-t border-af">
          <div className="px-3 py-2 mb-2">
            <div className="text-sm font-semibold text-af truncate" data-testid="sidebar-username">
              {user?.full_name}
            </div>
            <div className="text-xs text-af-muted truncate">{user?.email}</div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-af-secondary hover:bg-af-subtle hover:text-af transition-all"
          >
            <LogOut className="h-4 w-4" /> Déconnexion
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top mobile bar */}
        <header className="md:hidden flex items-center justify-between px-4 py-3 border-b border-af bg-af-card">
          <Link to="/app/dashboard" className="flex items-center gap-2">
            <Logo size={42} />
            <span className="font-display font-extrabold text-lg">AfriMeet</span>
          </Link>
          <button
            data-testid="mobile-logout"
            onClick={logout}
            className="text-sm text-af-secondary"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </header>

        {/* Mobile bottom nav */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-af-card border-t border-af flex justify-around py-2 z-50">
          {[...navItems, ...(user?.is_super_admin ? [adminNavItem] : [])].map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`${item.testId}-mobile`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-1.5 py-1 text-[10px] ${
                  isActive ? "text-terracotta" : "text-af-secondary"
                }`
              }
            >
              <item.icon className="h-5 w-5" />
              {item.short || item.label.split(" ")[0]}
            </NavLink>
          ))}
        </nav>

        <main className="flex-1 overflow-y-auto pb-20 md:pb-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
